if (!customElements.get('doll-gallery')) {
  customElements.define(
    'doll-gallery',
    class DollGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.step = Number.parseInt(this.dataset.step, 10) || 12;
        this.items = Array.from(this.querySelectorAll('[data-doll-gallery-item]'));
        this.triggers = Array.from(this.querySelectorAll('[data-doll-gallery-trigger]'));
        this.showMoreButton = this.querySelector('.product-doll-gallery__show-more');
        this.lightbox = this.querySelector('[data-doll-gallery-lightbox]');
        this.lightboxDialog = this.querySelector('.product-doll-gallery__lightbox-dialog');
        this.lightboxImage = this.querySelector('[data-doll-gallery-lightbox-image]');
        this.closeButton = this.querySelector('[data-doll-gallery-close]');
        this.previousButton = this.querySelector('[data-doll-gallery-prev]');
        this.nextButton = this.querySelector('[data-doll-gallery-next]');
        this.activeIndex = 0;
        this.previouslyFocusedElement = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.ignoreClickUntil = 0;

        this.showMoreButton?.addEventListener('click', () => this.showMore());
        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(Number.parseInt(trigger.dataset.dollGalleryIndex, 10) || 0);
          });
        });
        this.closeButton?.addEventListener('click', () => this.close());
        this.previousButton?.addEventListener('click', () => this.showPrevious());
        this.nextButton?.addEventListener('click', () => this.showNext());
        this.lightbox?.addEventListener('click', (event) => {
          if (Date.now() < this.ignoreClickUntil) return;

          if (
            event.target === this.lightbox ||
            event.target === this.lightboxDialog ||
            event.target === this.lightboxImage
          ) {
            this.close();
          }
        });
        this.lightbox?.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
        this.lightbox?.addEventListener('touchend', (event) => this.handleTouchEnd(event), { passive: false });
        document.addEventListener('keydown', (event) => this.handleKeydown(event));

        this.updateButton();
      }

      showMore() {
        const hiddenItems = this.items.filter((item) => item.hasAttribute('hidden'));
        const itemsToReveal = hiddenItems.slice(0, this.step);

        itemsToReveal.forEach((item) => {
          item.removeAttribute('hidden');
          this.loadDeferredImages(item);
        });

        this.updateButton();
      }

      loadDeferredImages(root) {
        root.querySelectorAll('img[data-deferred-section-media][data-src]').forEach((image) => {
          const src = image.getAttribute('data-src');
          const srcset = image.getAttribute('data-srcset');

          if (srcset) image.setAttribute('srcset', srcset);
          if (src) image.setAttribute('src', src);

          image.setAttribute('data-deferred-section-media-loaded', 'true');
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
        });
      }

      updateButton() {
        if (!this.showMoreButton) return;

        const hasMoreItems = this.items.some((item) => item.hasAttribute('hidden'));
        this.showMoreButton.hidden = !hasMoreItems;
      }

      open(index) {
        if (!this.lightbox || !this.lightboxImage || !this.triggers.length) return;

        this.previouslyFocusedElement = document.activeElement;
        this.lightbox.hidden = false;
        document.documentElement.classList.add('doll-gallery-lightbox-open');
        this.showImage(index);
        this.lightboxDialog?.focus();
      }

      close() {
        if (!this.lightbox) return;

        this.lightbox.hidden = true;
        document.documentElement.classList.remove('doll-gallery-lightbox-open');
        if (this.lightboxImage) {
          this.lightboxImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
          this.lightboxImage.width = 1;
          this.lightboxImage.height = 1;
          this.lightboxImage.alt = '';
        }
        this.previouslyFocusedElement?.focus?.();
      }

      showImage(index) {
        if (!this.lightboxImage || !this.triggers.length) return;

        this.activeIndex = (index + this.triggers.length) % this.triggers.length;
        const trigger = this.triggers[this.activeIndex];
        const thumbnail = trigger.querySelector('img');
        const fallbackSrc = trigger.dataset.fullSrc || trigger.href;
        const fullSrc = this.findLoadedProductImageSrc(trigger) || fallbackSrc;
        const fullWidth = Number.parseInt(trigger.dataset.fullWidth, 10) || thumbnail?.naturalWidth || 1;
        const fullHeight = Number.parseInt(trigger.dataset.fullHeight, 10) || thumbnail?.naturalHeight || 1;

        this.lightboxImage.width = fullWidth;
        this.lightboxImage.height = fullHeight;
        this.lightboxImage.src = fullSrc;
        this.lightboxImage.alt = thumbnail?.alt || '';
      }

      findLoadedProductImageSrc(trigger) {
        const mediaId = trigger.dataset.mediaId;
        const fallbackSrc = trigger.dataset.fullSrc || trigger.href;
        const candidates = [];

        if (mediaId) {
          document.querySelectorAll(`img[data-media-id="${mediaId}"]`).forEach((image) => candidates.push(image));
        }

        const fallbackKey = this.imageCacheKey(fallbackSrc);
        if (fallbackKey) {
          document.querySelectorAll('img').forEach((image) => {
            const imageSrc = image.currentSrc || image.src;

            if (this.imageCacheKey(imageSrc) === fallbackKey) candidates.push(image);
          });
        }

        const loadedImages = candidates.filter(
          (image, index) =>
            image !== this.lightboxImage &&
            candidates.indexOf(image) === index &&
            image.complete &&
            image.naturalWidth >= 700 &&
            (image.currentSrc || image.src),
        );

        loadedImages.sort((first, second) => second.naturalWidth - first.naturalWidth);

        return loadedImages[0]?.currentSrc || loadedImages[0]?.src || '';
      }

      imageCacheKey(src) {
        if (!src || src.startsWith('data:')) return '';

        try {
          const url = new URL(src, window.location.href);
          return `${url.origin}${url.pathname}`;
        } catch (error) {
          return '';
        }
      }

      showPrevious() {
        this.showImage(this.activeIndex - 1);
      }

      showNext() {
        this.showImage(this.activeIndex + 1);
      }

      handleTouchStart(event) {
        const touch = event.changedTouches?.[0];
        if (!touch) return;

        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }

      handleTouchEnd(event) {
        const touch = event.changedTouches?.[0];
        if (!touch) return;

        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        const didSwipe = this.handleSwipe(deltaX, deltaY);

        if (didSwipe) {
          event.preventDefault();
          this.ignoreClickUntil = Date.now() + 450;
        }
      }

      handleSwipe(deltaX, deltaY) {
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);

        if (horizontalDistance < 45 || verticalDistance > 80) return false;

        if (deltaX < 0) {
          this.showNext();
        } else {
          this.showPrevious();
        }

        return true;
      }

      handleKeydown(event) {
        if (!this.lightbox || this.lightbox.hidden) return;

        if (event.key === 'Escape') {
          this.close();
        } else if (event.key === 'ArrowLeft') {
          this.showPrevious();
        } else if (event.key === 'ArrowRight') {
          this.showNext();
        }
      }
    }
  );
}
